import {Big} from 'big.js';
import { CorridorBlock } from '../../core/models/corridor/corridor-block';
import { CorridorLevelBlock } from '../../core/models/corridor/corridor-level-block';
import { EnumCorridorColor } from '../../core/models/enum/enum-corridor-color';
import { Corridor } from '../../core/models/corridor/corridor';
import { MathUtils, bigToString, parseBig } from '../../core/utils/math-utils';


export class CorridorBlockService {


    getVirginCorridor(): CorridorBlock {
        const c1 = new CorridorLevelBlock(null, null, EnumCorridorColor.GREEN, false, false, "");
        const c2 = new CorridorLevelBlock(null, null, EnumCorridorColor.LIGHTGREEN, false, false, "");
        const c3 = new CorridorLevelBlock(null, null, EnumCorridorColor.ORANGE, false, false, "");
        const c4 = new CorridorLevelBlock(null, null, EnumCorridorColor.RED, false, false, "");
        const c5 = new CorridorLevelBlock(null, null, EnumCorridorColor.BLACK, false, false, "");
        const initializedCorridors = [c1, c2, c3, c4, c5];
        return new CorridorBlock(initializedCorridors, "0", "0", EnumCorridorColor.GRAY, "0");
    }

    getMatchedCorridor(appliedPrice: string, corridor: Corridor, nonNegociableArticleColor: string, discount: string): CorridorBlock {
        if (corridor.isNeutral) {
            return this.getNeutralCorridor(appliedPrice, corridor);
        }
        if (corridor.isFlat() && nonNegociableArticleColor) {
                const corridors = this.getCorridorBlocks(corridor, appliedPrice);
                corridors.forEach((corridorLevelBlock: CorridorLevelBlock) => {
                    corridorLevelBlock.matched = corridorLevelBlock.color === nonNegociableArticleColor;
                });
                return this.getMatchedCorridorBlock(corridors, appliedPrice, corridor, discount);  
        }
    
        const corridors = this.getCorridorBlocks(corridor, appliedPrice);
        return this.getMatchedCorridorBlock(corridors, appliedPrice, corridor, discount);
    }

    getNeutralCorridor(appliedPrice: string, corridor: Corridor): CorridorBlock{
        const corridors = [
            new CorridorLevelBlock(appliedPrice ? appliedPrice : corridor.p90, null, EnumCorridorColor.GRAY, false, false, ""),
            new CorridorLevelBlock(appliedPrice ? appliedPrice : corridor.p50p90, null, EnumCorridorColor.GRAY, false, false, ""),
            new CorridorLevelBlock(appliedPrice ? appliedPrice : corridor.p50, null, EnumCorridorColor.GRAY, false, false, ""),
            new CorridorLevelBlock(appliedPrice ? appliedPrice : corridor.p10p50, null, EnumCorridorColor.GRAY, false, false, ""),
            new CorridorLevelBlock(appliedPrice ? appliedPrice : corridor.p10, null, EnumCorridorColor.GRAY, false, false, ""),
          ];
        return new CorridorBlock(corridors, appliedPrice, null, EnumCorridorColor.GRAY, null);
    }    

    private getMatchedCorridorBlock(corridors: CorridorLevelBlock[], appliedPrice: string, corridor: Corridor, discount: string) {
        const matchedCorridor = corridors.find(corr => corr.matched);
        const distance = this.getAppliedPriceDistance(appliedPrice, corridor);

        return new CorridorBlock(corridors, appliedPrice, discount,
            matchedCorridor == null || matchedCorridor.isBelowBlack ? EnumCorridorColor.BLACK : matchedCorridor.color,
            distance
        );
    }

    private getCorridorBlocks(corridor: Corridor, appliedPrice: string) {
       //TODO ::
        return [
            new CorridorLevelBlock(corridor.p90, 'cible', EnumCorridorColor.GREEN, new Big(appliedPrice).gte(new Big(corridor.p90)), false, this.getTopStyle(corridor.p90, null, false, appliedPrice, corridor)),
            new CorridorLevelBlock(corridor.p50p90, this.getCorridorLevelBlockDiscountInPercentage(parseBig(corridor.r7090)), EnumCorridorColor.LIGHTGREEN, new Big(appliedPrice).gte(new Big(corridor.p50p90)) && new Big(appliedPrice).lt(new Big(corridor.p90)),
                false, this.getTopStyle(corridor.p50p90, corridor.p90, false, appliedPrice, corridor)),
            new CorridorLevelBlock(corridor.p50, this.getCorridorLevelBlockDiscountInPercentage(parseBig(corridor.r5090)), EnumCorridorColor.ORANGE, new Big(appliedPrice).gte(new Big(corridor.p50)) && new Big(appliedPrice).lt(new Big(corridor.p50p90)),
                false, this.getTopStyle(corridor.p50, corridor.p50p90, false, appliedPrice, corridor)),
            new CorridorLevelBlock(corridor.p10p50, this.getCorridorLevelBlockDiscountInPercentage(parseBig(corridor.r3090)), EnumCorridorColor.RED, new Big(appliedPrice).gte(new Big(corridor.p10p50)) && new Big(appliedPrice).lt(new Big(corridor.p50)),
                false, this.getTopStyle(corridor.p10p50, corridor.p50, false, appliedPrice, corridor)),
            new CorridorLevelBlock(corridor.p10, this.getCorridorLevelBlockDiscountInPercentage(parseBig(corridor.r1090)), EnumCorridorColor.BLACK, new Big(appliedPrice).lt(new Big(corridor.p10p50)), new Big(appliedPrice).lt(new Big(corridor.p10)),
                this.getTopStyle(corridor.p10, corridor.p10p50, true, appliedPrice, corridor)),
        ];
    }

    getCorridorLevelBlockDiscountInPercentage(r: Big): string {
        const result = MathUtils.bigIsNullOrZero(r) ? new Big("0") : r.mul(new Big("100"));
        const convertResultToNumber = Math.ceil(Number(result));
        return convertResultToNumber.toString();
    }

    private getTopStyle(currentP: string, previousP: string, isBelowBlack: boolean = false, appliedPrice: string, corridor: Corridor): string {
        if (parseBig(currentP).eq(parseBig(corridor.p90))) {
            return parseBig(currentP).eq(parseBig(appliedPrice)) ? '100' : '50';
        } else if (isBelowBlack && parseBig(appliedPrice).lt(parseBig(currentP))) {
            return '150';
        } else {
            const interval: Big = parseBig(previousP).minus(parseBig(currentP));
            const ecart: Big = parseBig(appliedPrice).minus(parseBig(currentP));
            const topStyle: Big = MathUtils.bigIsNullOrZero(interval)
                ? parseBig("100") : parseBig("100").minus(ecart.div(interval).mul(parseBig("100")));
            return topStyle.toFixed(0);
        }
    }


    getAppliedPriceDistance(newAppliedPrice: string, corridor: Corridor): string {

        const distance = new Big(corridor.p90).minus(new Big(corridor.p10)).eq(new Big(0)) ?
            new Big(0) :
            (new Big(corridor.p90).minus(new Big(newAppliedPrice)))
                .div(new Big(corridor.p90).minus(new Big(corridor.p10)));

        return bigToString(distance);
    }
}
